<template>
  <v-bottom-sheet
    v-model="isOpened"
    data-qa="core-bottom-sheet"
    v-bind="{
      ...$attrs,
      'content-class': $attrs['content-class']
        ? `${$attrs['content-class']} flex-dialog`
        : 'flex-dialog',
    }"
    v-on="$listeners"
  >
    <div
      :data-qa="dataQa"
      data-qa-base="core-bottom-sheet"
      class="core-bottom-sheet d-flex flex-column flex-grow-1 overflow-y-auto bg-gray-0 elevation-20 h-100 position-relative gr-6"
    >
      <div
        v-if="title || $slots.title"
        data-qa="core-bottom-sheet-toolbar-container"
        class="core-bottom-sheet__container flex-shrink-0"
      >
        <div
          data-qa="core-bottom-sheet-toolbar"
          class="core-bottom-sheet__toolbar"
        >
          <div class="core-bottom-sheet__header text-break">
            <div
              data-qa="core-bottom-sheet-title"
              class="text-h6 text--text mr-auto"
            >
              {{ title }}
              <slot name="title" />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="$scopedSlots.default || $slots.default"
        data-qa="core-bottom-sheet-content"
        class="core-bottom-sheet__container d-flex flex-column flex-fill overflow-y-auto custom-scroll"
      >
        <div
          class="d-flex flex-column flex-fill"
          :class="slotContentClass"
        >
          <slot
            x-space-class="px-4"
            counter-x-space-class="mx-n4"
          />
        </div>
      </div>

      <div
        v-if="$slots.actions"
        data-qa="core-bottom-sheet-actions-container"
        class="core-bottom-sheet__container flex-shrink-0 mt-auto"
      >
        <div
          data-qa="core-bottom-sheet-actions"
          class="core-bottom-sheet__actions d-flex align-center justify-end ga-2"
          :class="slotActionsClass"
        >
          <slot name="actions" />
        </div>
      </div>
    </div>
  </v-bottom-sheet>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const props = defineProps<{
  value: boolean
  title?: string
  dataQa?: string
  slotActionsClass?: string | string[] | Record<string, boolean>
  slotContentClass?: string | string[] | Record<string, boolean>
}>()

type Emits = {
  (_event: 'input', _value: boolean): void,
}

const emit = defineEmits<Emits>()

const isOpened = computed({
  get() {
    return props.value
  },
  set(value: boolean) {
    emit('input', value)
  },
})
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

:deep(.v-dialog.flex-dialog) {
  display: flex;
  flex-direction: column;
}

.core-bottom-sheet {
  max-height: 80vh;

  @supports (max-height: 80dvh) {
    max-height: 80dvh;
  }

  &__container {
    position: relative;

    & > :deep(*) {
      width: 100%;
      max-width: 720px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  & > .core-bottom-sheet__container {
    &:first-child {
      padding-top: 24px;
    }

    &:last-child {
      padding-bottom: 16px;
    }

    & > div {
      padding-left: 16px;
      padding-right: 16px;
    }

    & > .core-bottom-sheet__actions {
      & > :deep(.v-btn) {
        padding-left: 16px;
        padding-right: 16px;
      }
    }
  }
}

.custom-scroll {
  @supports (scrollbar-color: auto) {
    scrollbar-color: $ui-kit-bg-gray-80 $ui-kit-bg-gray-40;
  }

  @supports (selector(::-webkit-scrollbar)) {
    scrollbar-color: auto;

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-pill;
      background-color: $ui-kit-bg-gray-60;
    }

    &::-webkit-scrollbar {
      width: 8px;
      border-radius: $border-radius-pill;
      background-color: $ui-kit-bg-gray-40;
    }
  }
}
</style>
